import type { UserFromDb, Indexed, UserFromClient } from "shared"
import {
	useMountEffect,
	adminGetClientUsers,
	adminDeleteClientUser,
	ColorByValue,
	useQuery,
	useUpdateableHandler,
	DEFAULT_AVAILABLE_USERS,
	clientApi,
	naturalSortComparer,
	CardView,
	SearchBar,
	SortableTableCard,
	useSearch,
	getHumanReadableAge,
	RESTRICTABLE_FEATURES,
	SelectionContextProvider,
	useSelectionContext,
	useBatchProcessing,
} from "shared"
import { InlineLabelSwitch } from "shared/src/components/shadcn/components/atoms/switches"
import { Button } from "shared/src/components/shadcn/components/atoms/buttons"
import { Tooltip, TooltipTrigger, TooltipContent } from "shared/src/components/shadcn/components/ui/tooltip"
import { normalizeEmail } from "shared/src/libs/validationLib/validationLib"
import { useCallback, useState } from "react"
import { useHistory } from "react-router-dom"
import { UserInfoView } from "./UserInfoView"
import { getUserBadgeKind } from "./userBadge"
import "./TeamPage.css"
import EvoIcon from "shared/src/images/EvoIcon.png"

interface UserStats {
	invited?: number
	signedIn?: number
	activeInLastMonth?: number
}

function lastLoggedInWithinLastMonth(lastLogin: string): boolean {
	const now = new Date().getTime()
	const oneMonthAgo = now / 1000 - 60 * 60 * 24 * 30
	return parseInt(lastLogin) > oneMonthAgo
}

function getUserStats(users: Indexed<UserFromDb>): UserStats {
	const stats: UserStats = {
		invited: 0,
		signedIn: 0,
		activeInLastMonth: 0,
	}
	const now = new Date().getTime()
	const oneMonthAgo = now / 1000 - 60 * 60 * 24 * 30
	Object.values(users).forEach((user) => {
		stats.invited += 1
		if (user.lastLogin != null) {
			stats.signedIn += 1
		}
		if (user.lastLogin != null && parseInt(user.lastLogin) > oneMonthAgo) {
			stats.activeInLastMonth += 1
		}
	})
	return stats
}

function getUsersFromClient(users: Indexed<UserFromClient>, clientId: string): Indexed<UserFromDb> {
	return Object.entries(users ?? {}).reduce((obj, [userId, user]) => {
		// client.userlist mirrors the email in mixed case for legacy users until
		// the data migration cleans them up. Lowercase here so the synthesised
		// `miscData` matches the canonical form stored on the User row.
		const normalizedEmail = normalizeEmail(user?.email ?? "")
		return {
			...obj,
			[userId]: {
				pk: `cli#${clientId}`,
				sk: `usr#${userId}`,
				miscData: `email#${normalizedEmail}`,
				userId,
				name: user.name,
				email: normalizedEmail,
				userRole: user.user_role,
				lastLogin: user.lastLogin,
			},
		}
	}, {})
}

export function TeamPage({ clientId, data, setData }): JSX.Element {
	const history = useHistory()
	const query = useQuery()
	const [waiting, setWaiting] = useState<boolean>(false)
	let [users, setUsers] = useState<Indexed<UserFromDb>>({})
	const [userStats, setUserStats] = useState<UserStats>({})
	const clientUsers = getUsersFromClient(data.userlist, clientId) ?? {}
	const currentUser = query.get("userId")
	const newUserMode = query.get("newUserMode") === "true"
	const [localAvailableUsers, handleLocalAvailableUsers] = useUpdateableHandler(
		data?.limits?.users?.limit || DEFAULT_AVAILABLE_USERS
	)
	const [localEnforceUsersLimit, handleLocalEnforceUsersLimit] = useState(data?.limits?.users?.enforced || false)
	const [localShowLimitToUsers, handleLocalShowLimitToUsers] = useState(data?.limits?.users?.showToUsers || false)
	const [isLoading, setIsLoading] = useState(false)
	const setCurrentUserData = useCallback((data) => {
		if (data == null) {
			return
		}
		setUsers((users) => ({ ...users, [data.userId ?? data.user_id]: data }))
		// setUserStats(getUserStats(users))
	}, [])
	const handleUserDeleted = useCallback(
		(userId: string) => {
			setUsers((prev) => {
				const updated = { ...prev }
				delete updated[userId]
				setUserStats(getUserStats(updated))
				return updated
			})
			history.replace({ search: "" })
		},
		[history]
	)
	const updateUserLimit = (updates) => {
		setWaiting(true)
		const newLimit = {
			...data.limits,
			users: {
				...data?.limits?.users,
				...updates,
			},
		}
		const change = {
			limits: {
				users: {
					...updates,
				},
			},
		}

		const newData = { limits: newLimit }
		clientApi
			.patch({ clientId, updates: change, changesOnly: true })
			.then(() => {
				setData({ ...data, ...newData })
			})
			.catch(() => {
				alert("Failed to update. Refresh page to check current data")
			})
			.finally(() => {
				setWaiting(false)
			})
	}

	const setNewUserLimit = () => {
		updateUserLimit({ limit: parseInt(localAvailableUsers) })
	}

	const setEnforceUserLimit = (newValue) => {
		updateUserLimit({ limit: parseInt(localAvailableUsers), enforced: newValue })
		handleLocalEnforceUsersLimit(newValue)
	}

	const setShowLimitToUsers = (newValue) => {
		updateUserLimit({ showToUsers: newValue })
		handleLocalShowLimitToUsers(newValue)
	}

	const makeSearchString = useCallback((user: UserFromDb & { usageChange: number }) => {
		let base_search_string = `${user.name} ${user.email} ${user.userRole} ${user.contactType}}`
		Object.entries(SEARCH_TAGS).forEach(([key, { tag, test }]) => {
			if (test(user)) {
				base_search_string += ` ${tag}`
			}
		})
		return base_search_string
	}, [])

	const [searchTermRef, setSearchTerm, filteredUsers] = useSearch<UserFromDb>(users, makeSearchString, "team-table")

	const getUsers = (event?: React.MouseEvent<HTMLButtonElement>) => {
		setWaiting(true)
		adminGetClientUsers({
			clientId,
			stats: ["cvsUploaded60", "cvsUploaded30", "cvsUploadedMonth"],
		})
			.then((newUsers) => {
				const indexedUsers = newUsers.reduce(
					(obj, user) => ({
						...obj,
						[user.userId]: { ...user, usageChange: user.cvsUploaded30 * 2 - user.cvsUploaded60 },
					}),
					{}
				)
				users = { ...clientUsers, ...indexedUsers }
				setUsers(users)
				setUserStats(getUserStats(users))
			})
			.finally(() => {
				setWaiting(false)
			})
	}

	useMountEffect(() => {
		// fetch users for client
		getUsers()
	})

	const headings = [
		"User Name",
		"Email",
		"Role",
		"CVs Processed (last 30 days)",
		"Usage Change",
		"CVs Processed (Calendar Month)",
		"Last Login",
	]

	const infoKeys = ["name", "email", "userRole", "cvsUploaded30", "usageChange", "cvsUploadedMonth", "lastLogin"]
	const preprocessName = useCallback(
		(name: string, rowId: string) => {
			const user = users[rowId]
			const badgeKind = getUserBadgeKind(user)

			const badge =
				badgeKind === "evo" ? (
					<img src={EvoIcon} alt="Evo" style={{ height: 18 }} />
				) : badgeKind === "both" ? (
					<span
						style={{
							backgroundColor: "var(--ara-purple)",
							color: "white",
							fontWeight: "bold",
							padding: "2px 5px",
							borderRadius: "4px",
							fontSize: "11px",
							whiteSpace: "nowrap",
						}}>
						Both
					</span>
				) : badgeKind === "integration" ? (
					<span
						style={{
							backgroundColor: "var(--ara-gold)",
							color: "white",
							fontWeight: "bold",
							padding: "2px 5px",
							borderRadius: "4px",
							fontSize: "11px",
							whiteSpace: "nowrap",
						}}>
						Integration
					</span>
				) : null

			if (badge) {
				return (
					<span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
						{name}
						{badge}
					</span>
				)
			}
			return name
		},
		[users]
	)

	const preprocessing = [
		preprocessName,
		null,
		null,
		null,
		(a) => ColorByValue(a, a > 0 ? `+${a}` : a, 0, false),
		null,
		preprocessLastLogin,
	]

	const individualSortFuncs = [
		(a, b) => -naturalSortComparer(a.name, b.name),
		(a, b) => -naturalSortComparer(a.email, b.email),
		(a, b) => -naturalSortComparer(a.userRole, b.userRole),
		(a, b) => (b.cvsUploaded30 ?? 0) - (a.cvsUploaded30 ?? 0),
		(a, b) => (b.usageChange ?? 0) - (a.usageChange ?? 0),
		(a, b) => (b.cvsUploadedMonth ?? 0) - (a.cvsUploadedMonth ?? 0),
		(a, b) => (a.lastLogin ?? 0) - (b.lastLogin ?? 0),
	]

	const onClickNewUser = () => {
		history.replace({
			search: `newUserMode=true`,
		})
	}

	const showUserInfo = currentUser != null || newUserMode

	function handleToggleSSO() {
		setData({ ...data, ssoEnabled: !data.ssoEnabled })
		clientApi.patch({ clientId, updates: { ssoEnabled: !data.ssoEnabled } }).catch((error) => {
			console.error("Failed to update SSO setting:", error)
		})
	}

	return (
		<div
			className={`account-container team-container ${showUserInfo ? "team-container-userview" : ""}`}
			style={{ maxWidth: "90%", margin: "auto", gap: 16, display: "flex", flexDirection: "column" }}>
			<h1 className="bs-h1">Team</h1>
			<div
				style={{
					display: "flex",
					gap: 15,
					justifyContent: "center",
					alignItems: "center",
					fontSize: 18,
					fontWeight: 500,
				}}>
				<div style={{ display: "flex", gap: 5 }}>
					<div
						style={{
							fontWeight: 300,
						}}>
						Active in last month:
					</div>
					<div>{userStats.activeInLastMonth}</div>
				</div>
				<div style={{ display: "flex", gap: 5 }}>
					<div
						style={{
							fontWeight: 300,
						}}>
						Signed In:
					</div>
					<div>{userStats.signedIn}</div>
				</div>
				<div style={{ display: "flex", gap: 5 }}>
					<div
						style={{
							fontWeight: 300,
						}}>
						Invited:
					</div>
					<div>{userStats.invited}</div>
				</div>
			</div>

			<div style={{ textAlign: "center" }}>(Sign in has been recorded since July 2023)</div>
			<CardView>
				<h4 className="bs-h4">Set users limits</h4>
				<div style={{ width: "fit-content" }}>
					<Tooltip>
						<TooltipTrigger asChild>
							<div style={{ display: "flex", gap: 10, alignItems: "center", margin: "20px 0", width: "fit-content" }}>
								Show limits to users:{" "}
								<InlineLabelSwitch checked={localShowLimitToUsers} onCheckedChange={setShowLimitToUsers} />
							</div>
						</TooltipTrigger>
						<TooltipContent side="bottom" className="max-w-[200px]">
							When on, users will be able to see users added to the account and user limit in their settings page &gt;
							team tab.
						</TooltipContent>
					</Tooltip>
				</div>
				<>
					<div style={{ display: "flex", gap: 5, alignItems: "center", margin: "20px 0" }}>
						<span style={Object.keys(users).length > localAvailableUsers ? { color: "var(--ara-error)" } : {}}>
							Number of users: {Object.keys(users).length}
						</span>{" "}
						/
						<input
							style={{ width: 40 }}
							type="text"
							inputMode="numeric"
							pattern="[0-9]*"
							value={localAvailableUsers}
							onChange={handleLocalAvailableUsers}></input>
						<Button style={{ marginTop: 0 }} loading={isLoading} onClick={setNewUserLimit}>
							Set New Limit
						</Button>
					</div>
					<div style={{ width: "fit-content" }}>
						<Tooltip>
							<TooltipTrigger asChild>
								<div style={{ display: "flex", gap: 10, alignItems: "center", margin: "20px 0", width: "fit-content" }}>
									Enforce users limits:{" "}
									<InlineLabelSwitch checked={localEnforceUsersLimit} onCheckedChange={setEnforceUserLimit} />
								</div>
							</TooltipTrigger>
							<TooltipContent side="bottom" className="max-w-[200px]">
								When on, admins will have to delete some users before being able to add new users if the number of users
								exceeds the limit.
							</TooltipContent>
						</Tooltip>
					</div>
					<div
						style={{
							width: "fit-content",
							display: "flex",
							alignItems: "center",
							justifyContent: "flex-start",
							gap: 10,
						}}>
						SSO :{" "}
						{data.ssoEnabled ? (
							"Enabled"
						) : (
							<div style={{ width: "45px" }}>
								<InlineLabelSwitch checked={data.ssoEnabled} onCheckedChange={handleToggleSSO} />
							</div>
						)}
					</div>
				</>
			</CardView>
			<SelectionContextProvider ids={Object.values(filteredUsers).map((u) => u.userId)}>
				<div
					style={{
						position: "relative",
						height: 50,
						width: "100%",
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}>
					<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
						{data.evoEnabled ? (
							<Tooltip>
								<TooltipTrigger asChild>
									<span>
										<Button disabled>
											<span>+ Users</span>
										</Button>
									</span>
								</TooltipTrigger>
								<TooltipContent className="admin-search-tooltip">
									<div>Adding new users is disabled for Evo enabled clients.</div>
									<div>New users must be added directly from the Evo platform user management.</div>
								</TooltipContent>
							</Tooltip>
						) : (
							<Button onClick={onClickNewUser}>
								<span>+ Users</span>
							</Button>
						)}
						<BulkDeleteButton clientId={clientId} users={users} onUsersDeleted={handleUserDeleted} />
					</div>
					<div>
						<Tooltip>
							<TooltipTrigger asChild>
								<SearchBar searchTermRef={searchTermRef} setSearchTerm={setSearchTerm} />
							</TooltipTrigger>
							<TooltipContent side="left" className="admin-search-tooltip w-[400px]">
								{makeTooltipText(searchTermRef.current)}
							</TooltipContent>
						</Tooltip>
					</div>
				</div>

				<SortableTableCard
					refreshProps={{ onClick: getUsers, isRefreshing: waiting }}
					info={Object.values(filteredUsers)}
					headings={showUserInfo ? [headings[0]] : headings}
					preprocessing={showUserInfo ? [preprocessing[0]] : preprocessing}
					infoKeys={showUserInfo ? [infoKeys[0]] : infoKeys}
					rowIdKey={"userId"}
					rowFunc={(userId) => {
						history.replace({
							search: `userId=${userId}`,
						})
					}}
					sortFunc={(a, b) => naturalSortComparer(a.name, b.name)}
					individualSortFuncs={showUserInfo ? [individualSortFuncs[0]] : individualSortFuncs}>
					{showUserInfo && (
						<div className="user-info-container">
							<UserInfoView
								users={users}
								clientId={clientId}
								client={data}
								newUserMode={newUserMode}
								user={users[currentUser]}
								setUserData={setCurrentUserData}
								onUserDeleted={handleUserDeleted}
							/>
						</div>
					)}
				</SortableTableCard>
			</SelectionContextProvider>
		</div>
	)
}

function BulkDeleteButton({
	clientId,
	users,
	onUsersDeleted,
}: {
	clientId: string
	users: Indexed<UserFromDb>
	onUsersDeleted: (userId: string) => void
}) {
	const { selectedIds, selectedCount, clearSelection } = useSelectionContext()
	const [isDeleting, runInBatches] = useBatchProcessing()

	const deleteUser = useCallback(
		async (userId: string) => {
			const user = users[userId]
			await adminDeleteClientUser({ clientId, userId, email: user?.email })
			onUsersDeleted(userId)
		},
		[clientId, users, onUsersDeleted]
	)

	const handleBulkDelete = useCallback(async () => {
		const message = `Are you sure you want to delete ${selectedCount} user${selectedCount === 1 ? "" : "s"}?`
		if (!window.confirm(message)) return
		const failures = await runInBatches(deleteUser, selectedIds)
		const numFails = Object.keys(failures ?? {}).length
		if (numFails > 0) {
			alert(`Failed to delete ${numFails} user${numFails === 1 ? "" : "s"}`)
		}
		clearSelection()
	}, [selectedIds, selectedCount, deleteUser, runInBatches, clearSelection])

	if (selectedCount === 0) return null

	return (
		<Button
			variant="destructive"
			onClick={() => {
				handleBulkDelete()
			}}
			loading={isDeleting}
			disabled={isDeleting}>
			Delete {selectedCount} user{selectedCount === 1 ? "" : "s"}
		</Button>
	)
}

function preprocessLastLogin(unixTimestamp) {
	const loggedInThisMonth = lastLoggedInWithinLastMonth(unixTimestamp)
	const readableDate = getHumanReadableAge(unixTimestamp)
	return <span className={loggedInThisMonth ? "greenText" : "redText"}>{readableDate}</span>
}

const BETA_SEARCH_TAGS: {
	[key: string]: { tag: string; description: string; test: (user: UserFromDb & { usageChange: number }) => boolean }
} = Object.entries(RESTRICTABLE_FEATURES).reduce((obj, [key, value]) => {
	if (value.releaseState !== "beta") {
		return obj
	}
	return {
		...obj,
		[key]: {
			tag: `@beta:${key}`,
			description: `Beta access to ${value.label}`,
			test: (user) => user.betaPermissions?.[key] ?? false,
		},
	}
}, {})

const SEARCH_TAGS: {
	[key: string]: { tag: string; description: string; test: (user: UserFromDb & { usageChange: number }) => boolean }
} = {
	...BETA_SEARCH_TAGS,
	betaSite: {
		tag: "@beta:siteAccess",
		description: "Beta Site Access",
		test: (user) => user.userType === "beta",
	},
	usageChangeLessThan0: {
		tag: "@uc<0",
		description: "Usage going down in last month",
		test: (user) => user.usageChange < 0,
	},
	invitationUnaccepted: {
		tag: "@ui",
		description: "Unaccepted Invitation",
		test: (user) => user.lastLogin == null,
	},
	lastLoginOverAMonthAgo: {
		tag: "@ll>30",
		description: "Last login over a month ago",
		test: (user) =>
			user.lastLogin != null && parseInt(user.lastLogin) < new Date().getTime() / 1000 - 60 * 60 * 24 * 30,
	},
	lastLoginOver2MonthsAgo: {
		tag: "@ll>60",
		description: "Last login over 2 months ago",
		test: (user) =>
			user.lastLogin != null && parseInt(user.lastLogin) < new Date().getTime() / 1000 - 60 * 60 * 24 * 60,
	},
}

function makeTooltipText(search) {
	return (
		<div>
			{Object.values(SEARCH_TAGS).map(({ tag, description }) => (
				<div style={{ display: "flex", color: tag.includes(search) ? "white" : "#aaaaaa" }} key={tag}>
					<div style={{ width: 200 }}>{tag}:</div>
					<div>{description}</div>
				</div>
			))}
		</div>
	)
}
